import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { API_BASE } from "@/lib/env";
import { siteConfig } from "@/lib/siteConfig";
import { getStoredPublicLanguage, PUBLIC_LANGUAGE_CHANGE_EVENT } from "@/lib/cmsI18n";

const Ctx = createContext(null);

function applyFaviconFromSettings(settings) {
  /** SiteLogo ile aynı mantık: ayarlarda URL yoksa public/logo.svg */
  const href =
    typeof settings?.logoImageUrl === "string" && settings.logoImageUrl.trim()
      ? settings.logoImageUrl.trim()
      : "/logo.svg";
  let icon = document.getElementById("dekosta-favicon");
  if (!icon) {
    icon = document.createElement("link");
    icon.id = "dekosta-favicon";
    icon.rel = "icon";
    document.head.appendChild(icon);
  }
  icon.setAttribute("href", href);
  if (/\.svg(\?|$)/i.test(href)) {
    icon.setAttribute("type", "image/svg+xml");
  } else {
    icon.removeAttribute("type");
  }

  let apple = document.getElementById("dekosta-apple-touch-icon");
  if (!apple) {
    apple = document.createElement("link");
    apple.id = "dekosta-apple-touch-icon";
    apple.rel = "apple-touch-icon";
    document.head.appendChild(apple);
  }
  apple.setAttribute("href", href);
}

function applyDocumentHead(settings, lang) {
  if (!settings) return;
  const lng = lang === "en" ? "en" : "tr";
  const titleTr =
    typeof settings.seo_title === "string" && settings.seo_title.trim()
      ? settings.seo_title.trim()
      : `${siteConfig.brand} · Hızlı 3D Baskı ve Prototipleme`;
  const titleEnOpt =
    typeof settings.seo_title_en === "string" && settings.seo_title_en.trim()
      ? settings.seo_title_en.trim()
      : "";
  const title = lng === "en" ? titleEnOpt || titleTr : titleTr;
  document.title = title;
  applyFaviconFromSettings(settings);
  const descTr =
    typeof settings.seo_description === "string" && settings.seo_description.trim()
      ? settings.seo_description.trim()
      : "";
  const descEnOpt =
    typeof settings.seo_description_en === "string" && settings.seo_description_en.trim()
      ? settings.seo_description_en.trim()
      : "";
  const desc = lng === "en" ? descEnOpt || descTr : descTr;
  if (desc) {
    let m = document.querySelector('meta[name="description"]');
    if (!m) {
      m = document.createElement("meta");
      m.setAttribute("name", "description");
      document.head.appendChild(m);
    }
    m.setAttribute("content", desc);
  }
}

/**
 * Loads dynamic public data (settings, services, materials, portfolio, faqs)
 * once at app mount and refreshes on request. Public pages use this instead of
 * the static siteConfig defaults (which remain as fallback).
 */
export function PublicDataProvider({ children }) {
  const [data, setData] = useState({ settings: null, services: null, materials: null, portfolio: null, faqs: null });
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const [s, sv, ma, po, fa] = await Promise.all([
        axios.get(`${API_BASE}/public/settings`),
        axios.get(`${API_BASE}/public/services`),
        axios.get(`${API_BASE}/public/materials`),
        axios.get(`${API_BASE}/public/portfolio`),
        axios.get(`${API_BASE}/public/faqs`),
      ]);
      setData({
        settings: s.data,
        services: sv.data,
        materials: ma.data,
        portfolio: po.data,
        faqs: fa.data,
      });
    } catch (e) {
      // fail silently — public pages fall back to static defaults
      console.warn("public data load failed", e);
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!loaded || !data.settings) return;
    const run = () => applyDocumentHead(data.settings, getStoredPublicLanguage());
    run();
    window.addEventListener(PUBLIC_LANGUAGE_CHANGE_EVENT, run);
    window.addEventListener("storage", run);
    return () => {
      window.removeEventListener(PUBLIC_LANGUAGE_CHANGE_EVENT, run);
      window.removeEventListener("storage", run);
    };
  }, [loaded, data.settings]);

  return <Ctx.Provider value={{ ...data, loaded, reload: load }}>{children}</Ctx.Provider>;
}

export function usePublicData() {
  return useContext(Ctx) || { settings: null, services: null, materials: null, portfolio: null, faqs: null, loaded: false };
}
